import { Inject, Injectable } from '@angular/core';
import { ApplicationInsights, IMetricTelemetry } from '@microsoft/applicationinsights-web';
import { AuthenticationService, LicenceInfoService, AppSettingsService } from '@nx-fe/services';
import { take } from 'rxjs';
import * as crypto from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private events: { [key: string]: number } = {};
  private appInsights: ApplicationInsights;
  private xsightInstallationId: string;
  private azureApiKey: string;
  private readonly DATA_TO_SYNC: string = 'analyticsData';

  constructor(
    private _authenticationService: AuthenticationService,
    private _licenseInfoService: LicenceInfoService,
    private _appSettingsService: AppSettingsService,
    @Inject('LOCALSTORAGE')
    private _localStorage: Storage
  ) {
    this.setUserInfo();
    this.syncUnsavedData();
    this.startPeriodicSync();
    window.addEventListener('beforeunload', () => this.onBeforeUnload());
  }

  private syncUnsavedData() {
    const data = this.loadEventsFromLocalStorage();
    if (data) {
      this.syncData(data.events, data.userInfo);
    }
  }

  private loadEventsFromLocalStorage = (): EventUserInfo => {
    const data = this._localStorage.getItem(this.DATA_TO_SYNC);
    if (data) {
      this._localStorage.removeItem(this.DATA_TO_SYNC);
      return JSON.parse(data);
    }
    return null;
  };

  private storeEventsInLocalStorage() {
    const data: EventUserInfo = {
      events: this.events,
      userInfo: this.userInfo,
    };
    this._localStorage.setItem(this.DATA_TO_SYNC, JSON.stringify(data));
  }

  private onBeforeUnload() {
    if (Object.keys(this.events).length !== 0) {
      this.storeEventsInLocalStorage();
      this.syncData();
      this.appInsights.flush(false, () => {
        this._localStorage.removeItem(this.DATA_TO_SYNC);
      });
    }
  }

  private setUserInfo() {
    this._appSettingsService.loadAzureKey().then(resp => {
      this.azureApiKey = resp.azureApiKey;
      if (resp.azureConnectionString) {
        this.appInsights = new ApplicationInsights({
          config: {
            disableFetchTracking: true,
            disableAjaxTracking: true,
            loggingLevelConsole: 0,
            connectionString: resp.azureConnectionString,
            maxBatchInterval: 10000,
          },          
        });
        this.appInsights.loadAppInsights();
      }
    });
    this._licenseInfoService
      .getLicenceInformation()
      .pipe(take(1))
      .subscribe(data => {
        this.xsightInstallationId = data.xsightInstallationId;
      });
  }

  private get userId(): string {
    return this._authenticationService.getUserId();
  }

  private get userInfo(): UserInfo {
    return {
      userId: this.hashString(this.userId),
      xsightInstallationId: this.encryptString(this.xsightInstallationId),
      azureApiKey: this.azureApiKey,
    };
  }

  private resetCounts(): void {
    this.events = {};
  }

  private getEventCounts(events: { [key: string]: number }): IMetricTelemetry[] {
    return Object.entries(events).map(([name, average]) => ({ name, average }));
  }

  private hashString(input: string): string {
    return crypto.SHA256(input).toString(crypto.enc.Hex);
  }

  private encryptString(input: string): string {
    return Buffer.from(input).toString('base64');
  }

  updateEventCount(eventType: string): void {
    if (this.events[eventType] != null) {
      this.events[eventType]++;
    } else {
      this.events[eventType] = 1;
    }
  }

  syncData(data: { [key: string]: number } = this.events, userInfo = this.userInfo) {
    if (this.appInsights) {
      if (Object.keys(data).length !== 0) {
        const events = this.getEventCounts(data);
        for (let event of events) {
          this.appInsights.trackMetric(event, userInfo);
        }
        this.resetCounts();
      }
    }
  }

  startPeriodicSync(interval: number = 15000): void {
    setInterval(() => {
      if (Object.keys(this.events).length !== 0) {
        this.syncData();
      }
    }, interval);
  }
}

interface EventUserInfo {
  events: { [key: string]: number };
  userInfo: UserInfo;
}

interface UserInfo {
  userId: string;
  xsightInstallationId: string;
  azureApiKey: string;
}
